import React, { useState, useEffect, useRef } from "react";
import { DatePicker, Select, Space, Button, Dropdown } from "antd";
import dayjs, { Dayjs } from "dayjs";
import type { MenuProps } from "antd";
import locale from "antd/es/date-picker/locale/es_ES";

export enum DateFilterType {
  MONTH = "month",
  DAY = "day",
  RANGE = "range",
}

export interface DateFilterConfig {
  allowedTypes?: DateFilterType[];
  defaultType?: DateFilterType;
  forceType?: DateFilterType;
  showQuickFilters?: boolean;
  maxDate?: Dayjs;
  minDate?: Dayjs;
}

interface DateRangePickerProps {
  config?: DateFilterConfig;
  rangeTimeValue: [dayjs.Dayjs, dayjs.Dayjs];
  setRangeTimeValue: React.Dispatch<
    React.SetStateAction<[dayjs.Dayjs, dayjs.Dayjs]>
  >;
  style?: React.CSSProperties;
}

// Tipos específicos para los eventos de cambio
type SinglePickerValue = Dayjs | null;
type RangePickerValue = [Dayjs | null, Dayjs | null] | null;

// Interfaz para guardar la configuración en localStorage
interface StoredFilterState {
  selectedType: DateFilterType;
  rangeTimeValue: [string, string];
}

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  config = {},
  rangeTimeValue,
  setRangeTimeValue,
  style = { width: "100%" },
}) => {
  const {
    allowedTypes = [
      DateFilterType.MONTH,
      DateFilterType.DAY,
      DateFilterType.RANGE,
    ],
    defaultType = DateFilterType.MONTH,
    forceType,
    showQuickFilters = true,
    maxDate = dayjs().endOf("day"),
    minDate,
  } = config;

  const isInitialized = useRef(false);

  // Estado inicial que recupera de localStorage o usa el valor por defecto
  const [selectedType, setSelectedType] = useState<DateFilterType>(() => {
    // Si hay forceType, siempre usar ese
    if (forceType) return forceType;

    const stored = getStoredFilterState();
    if (stored) {
      // Verificar si el tipo almacenado es compatible con la configuración actual
      if (isTypeCompatible(stored.selectedType, allowedTypes)) {
        return stored.selectedType;
      }
    }

    // Si no hay stored o no es compatible, usar defaultType
    return defaultType;
  });

  // Efecto para manejar cambios en forceType desde las props
  useEffect(() => {
    if (forceType && forceType !== selectedType) {
      setSelectedType(forceType);
    }
  }, [forceType]);

  // Efecto para inicializar el rango desde localStorage
  useEffect(() => {
    if (isInitialized.current) return;

    const stored = getStoredFilterState();
    if (stored && isTypeCompatible(stored.selectedType, allowedTypes)) {
      try {
        const storedRange: [Dayjs, Dayjs] = [
          dayjs(stored.rangeTimeValue[0]),
          dayjs(stored.rangeTimeValue[1]),
        ];

        // Verificar que el rango almacenado sea válido para el tipo actual
        if (isRangeValidForType(storedRange, selectedType)) {
          setRangeTimeValue(storedRange);
          isInitialized.current = true;
          return;
        }
      } catch (error) {
        console.error("Error parsing stored date range:", error);
      }
    }

    // Si no hay stored o no es compatible, ajustar el rango actual
    if (shouldAdjustRange(rangeTimeValue, selectedType)) {
      const adjustedRange = adjustRangeToType(rangeTimeValue, selectedType);
      setRangeTimeValue(adjustedRange);
    }

    isInitialized.current = true;
  }, []);

  // Efecto separado para ajustar el rango cuando cambia el tipo
  useEffect(() => {
    if (!isInitialized.current) return;

    if (shouldAdjustRange(rangeTimeValue, selectedType)) {
      const adjustedRange = adjustRangeToType(rangeTimeValue, selectedType);
      setRangeTimeValue(adjustedRange);
    }
  }, [selectedType]);

  // Efecto para guardar en localStorage cuando cambie el estado
  useEffect(() => {
    if (!isInitialized.current) return;

    saveFilterStateToStorage(selectedType, rangeTimeValue);
  }, [selectedType, rangeTimeValue]);

  const showTypeSelector = !forceType && allowedTypes.length > 1;

  const handleTypeChange = (type: DateFilterType) => {
    setSelectedType(type);
  };

  const handleSingleDateChange = (date: SinglePickerValue) => {
    let newRange: [Dayjs, Dayjs];

    switch (selectedType) {
      case DateFilterType.MONTH:
        if (date) {
          newRange = [date.startOf("month"), date.endOf("month")];
        } else {
          newRange = getDefaultRange(selectedType);
        }
        break;

      case DateFilterType.DAY:
        if (date) {
          newRange = [date.startOf("day"), date.endOf("day")];
        } else {
          newRange = getDefaultRange(selectedType);
        }
        break;

      default:
        newRange = getDefaultRange(selectedType);
    }

    setRangeTimeValue(newRange);
  };

  const handleRangeDateChange = (dates: RangePickerValue) => {
    if (selectedType !== DateFilterType.RANGE) return;

    let newRange: [Dayjs, Dayjs];

    if (dates && dates[0] && dates[1]) {
      newRange = [dates[0].startOf("month"), dates[1].endOf("month")];
    } else {
      newRange = getDefaultRange(selectedType);
    }

    setRangeTimeValue(newRange);
  };

  // Filtros rápidos para MES
  const getMonthQuickFilters = (): MenuProps["items"] => [
    {
      key: "current-month",
      label: "Mes actual",
      onClick: () => {
        const newRange: [Dayjs, Dayjs] = [
          dayjs().startOf("month"),
          dayjs().endOf("month"),
        ];
        setRangeTimeValue(newRange);
      },
    },
    {
      key: "previous-month",
      label: "Mes anterior",
      onClick: () => {
        const prevMonth = dayjs().subtract(1, "month");
        const newRange: [Dayjs, Dayjs] = [
          prevMonth.startOf("month"),
          prevMonth.endOf("month"),
        ];
        setRangeTimeValue(newRange);
      },
    },
  ];

  // Filtros rápidos para DÍA
  const getDayQuickFilters = (): MenuProps["items"] => [
    {
      key: "today",
      label: "Hoy",
      onClick: () => {
        const newRange: [Dayjs, Dayjs] = [
          dayjs().startOf("day"),
          dayjs().endOf("day"),
        ];
        setRangeTimeValue(newRange);
      },
    },
    {
      key: "yesterday",
      label: "Ayer",
      onClick: () => {
        const yesterday = dayjs().subtract(1, "day");
        const newRange: [Dayjs, Dayjs] = [
          yesterday.startOf("day"),
          yesterday.endOf("day"),
        ];
        setRangeTimeValue(newRange);
      },
    },
  ];

  const getPlaceholder = (): string | [string, string] => {
    switch (selectedType) {
      case DateFilterType.RANGE:
        return ["Mes inicio", "Mes fin"];
      case DateFilterType.DAY:
        return "Selecciona un día";
      case DateFilterType.MONTH:
      default:
        return "Selecciona un mes";
    }
  };

  const renderDatePicker = () => {
    const commonSingleProps = {
      style: showQuickFilters ? { width: "calc(100% - 50px)" } : style,
      value: rangeTimeValue[0],
      onChange: handleSingleDateChange,
      placeholder: getPlaceholder() as string,
      disabledDate: (current: Dayjs) => {
        if (current && maxDate && current > maxDate) return true;
        if (current && minDate && current < minDate) return true;
        return false;
      },
      allowClear: false,
    };

    const commonRangeProps = {
      style: showQuickFilters ? { width: "calc(100% - 50px)" } : style,
      value: rangeTimeValue as [Dayjs, Dayjs],
      onChange: handleRangeDateChange,
      placeholder: getPlaceholder() as [string, string],
      disabledDate: (current: Dayjs) => {
        if (current && maxDate && current > maxDate) return true;
        if (current && minDate && current < minDate) return true;
        return false;
      },
      allowClear: false,
    };

    if (selectedType === DateFilterType.RANGE) {
      return (
        <DatePicker.RangePicker
          {...commonRangeProps}
          picker="month"
          format="MMMM YYYY"
          locale={locale}
        />
      );
    }

    return (
      <DatePicker
        {...commonSingleProps}
        picker={selectedType === DateFilterType.DAY ? "date" : "month"}
        format={
          selectedType === DateFilterType.DAY ? "DD/MM/YYYY" : "MMMM YYYY"
        }
        locale={locale}
      />
    );
  };

  const renderQuickFilters = () => {
    if (!showQuickFilters) return null;

    let quickFilters: MenuProps["items"] = [];

    switch (selectedType) {
      case DateFilterType.MONTH:
        quickFilters = getMonthQuickFilters();
        break;
      case DateFilterType.DAY:
        quickFilters = getDayQuickFilters();
        break;
      case DateFilterType.RANGE:
        // No hay filtros rápidos para rango
        return null;
    }

    return (
      <Dropdown menu={{ items: quickFilters }} placement="bottomRight">
        <Button style={{ width: "40px" }}>⚡</Button>
      </Dropdown>
    );
  };

  return (
    <Space.Compact style={style}>
      {showTypeSelector && (
        <Select
          value={selectedType}
          onChange={handleTypeChange}
          style={{ width: "120px" }}
        >
          {allowedTypes.map((type) => (
            <Select.Option key={type} value={type}>
              {getTypeLabel(type)}
            </Select.Option>
          ))}
        </Select>
      )}
      {renderDatePicker()}
      {renderQuickFilters()}
    </Space.Compact>
  );
};

// Función para guardar el estado en localStorage
function saveFilterStateToStorage(
  selectedType: DateFilterType,
  rangeTimeValue: [Dayjs, Dayjs]
) {
  const stateToSave: StoredFilterState = {
    selectedType,
    rangeTimeValue: [
      rangeTimeValue[0].toISOString(),
      rangeTimeValue[1].toISOString(),
    ],
  };
  localStorage.setItem("dateFilterConfig", JSON.stringify(stateToSave));
}

// Función para recuperar el estado desde localStorage
function getStoredFilterState(): StoredFilterState | null {
  try {
    const stored = localStorage.getItem("dateFilterConfig");
    if (stored) {
      return JSON.parse(stored) as StoredFilterState;
    }
  } catch (error) {
    console.error("Error reading stored filter state:", error);
  }
  return null;
}

// Función para verificar si un tipo es compatible con la configuración actual
function isTypeCompatible(
  type: DateFilterType,
  allowedTypes: DateFilterType[]
): boolean {
  return allowedTypes.includes(type);
}

// Función para verificar si un rango es válido para un tipo específico
function isRangeValidForType(
  range: [Dayjs, Dayjs],
  type: DateFilterType
): boolean {
  const [start, end] = range;

  // Verificar que las fechas sean válidas
  if (!start.isValid() || !end.isValid()) return false;

  // Verificar que el rango sea consistente con el tipo
  switch (type) {
    case DateFilterType.DAY:
      return start.isSame(end, "day");

    case DateFilterType.MONTH:
      return (
        start.isSame(end, "month") &&
        start.isSame(start.startOf("month")) &&
        end.isSame(end.endOf("month"))
      );

    case DateFilterType.RANGE:
      return (
        start.isSame(start.startOf("month")) && end.isSame(end.endOf("month"))
      );

    default:
      return true;
  }
}

function shouldAdjustRange(
  range: [Dayjs, Dayjs],
  type: DateFilterType
): boolean {
  const [start, end] = range;

  switch (type) {
    case DateFilterType.DAY:
      return !start.isSame(end, "day");

    case DateFilterType.MONTH:
      return (
        !start.isSame(end, "month") ||
        !start.isSame(start.startOf("month")) ||
        !end.isSame(end.endOf("month"))
      );

    case DateFilterType.RANGE:
      return (
        !start.isSame(start.startOf("month")) || !end.isSame(end.endOf("month"))
      );

    default:
      return false;
  }
}

function getDefaultRange(type: DateFilterType): [Dayjs, Dayjs] {
  const now = dayjs();

  switch (type) {
    case DateFilterType.DAY:
      return [now.startOf("day"), now.endOf("day")];

    case DateFilterType.MONTH:
      return [now.startOf("month"), now.endOf("month")];

    case DateFilterType.RANGE:
      return [now.startOf("month"), now.endOf("month")];

    default:
      return [now.startOf("month"), now.endOf("month")];
  }
}

function getTypeLabel(type: DateFilterType): string {
  switch (type) {
    case DateFilterType.DAY:
      return "Por día";
    case DateFilterType.MONTH:
      return "Mes";
    case DateFilterType.RANGE:
      return "Rango";
    default:
      return "Fecha";
  }
}

function adjustRangeToType(
  range: [Dayjs, Dayjs],
  type: DateFilterType
): [Dayjs, Dayjs] {
  const [start, end] = range;

  switch (type) {
    case DateFilterType.DAY:
      return [start.startOf("day"), start.endOf("day")];

    case DateFilterType.MONTH:
      return [start.startOf("month"), start.endOf("month")];

    case DateFilterType.RANGE:
      return [start.startOf("month"), end.endOf("month")];

    default:
      return getDefaultRange(type);
  }
}
